'use client';

import {
  getPersistedPin,
  setPersistedPin,
} from '@modules/prism-client/lib/manifest/pin-storage';
import {
  ManifestOverview,
  type ManifestOverviewProps,
} from '@tuic/web-compositions/my-trips/ManifestOverview';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import type { ComponentBlock } from '@modules/prism-client/types/types';

export type ManifestOverviewBlockProps = ManifestOverviewProps;

export const ManifestOverviewBlock: ComponentBlock<
  ManifestOverviewBlockProps
> = ({ data, fallback }) => {
  const [mounted, setMounted] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setMounted(true);

    // Skip overview and go directly to form if there's only one traveller
    if (data?.travellers?.length === 1 && data.travellers[0].href) {
      router.replace(data.travellers[0].href);
    }
  }, []);

  const isPinRequired = (travellerId: string): boolean => {
    if (!mounted) return false;

    const traveller = data?.travellers?.find(t => t.id === travellerId);

    // In case the traveller is not found -> fallback to not required
    if (!traveller) return false;

    const pin = traveller.pin;

    // If pin is not defined (missing passport number) -> no pin dialog required
    if (!pin) return false;

    const persistedPin = getPersistedPin(traveller.mpi);

    // If persisted pin is correct -> no pin dialog required
    if (pin === persistedPin) return false;

    // In any other case pin dialog is required
    return true;
  };

  const handlePinEnter = (travellerId: string, enteredPin: string): boolean => {
    const traveller = data?.travellers?.find(t => t.id === travellerId);

    // In case the traveller is not found -> return false
    if (!traveller) return false;

    const pin = traveller.pin;

    // If the entered pin does not match -> return false
    if (pin !== enteredPin) return false;

    // Set the pin on success input for later usage.
    setPersistedPin(traveller.mpi, enteredPin);

    return true;
  };

  return (
    <ManifestOverview
      {...data}
      fallback={fallback ?? data?.fallback}
      isPinRequired={isPinRequired}
      onPinEnter={handlePinEnter}
    />
  );
};
